// import { useState } from 'react'
import { BlackArrowLeftIcon, BlackArrowRightIcon } from '../icons/arrows.tsx'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { cn } from '../utilities'

interface PaginationProps {
  itemsPerPage: number
  currentPage: number
  totalRows: number
  onPageChange: (page: number) => void
  onPerPageChange?: (value: number) => void
}

const PerPageOptions = [5, 10, 20, 30, 50]
const LIMITPAGE = 10;
const Pagination = ({
  itemsPerPage,
  currentPage,
  totalRows,
  onPageChange,
  onPerPageChange
}: PaginationProps) => {
  const { t: translate } = useTranslation()
  const totalPages = Math.ceil(totalRows / itemsPerPage)
  const [perPage, setPerPage] = useState(itemsPerPage)
  const handlePerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setPerPage(parseInt(event.target.value))
    onPerPageChange && onPerPageChange(parseInt(event.target.value))
  }

  return (
    <div className="flex items-center justify-between py-3 w-full">
      {totalPages > 1 && <div className="flex flex-1 justify-between px-5 sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-muted-white"
        >
          {translate('previous')}
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-muted-white"
        >
          {translate('next')}
        </button>
      </div>
      }
      <div className="hidden sm:block xl:flex sm:flex-1 sm:items-center sm:justify-between w-full">
        <div className="flex justify-center xl:justify-start text-sm text-gray-700 flex items-center xl:m-0 xl:p-0 m-auto pb-5 w-[180px] gap-2">
          <div>
          {translate('show')}
          </div>
          <div className="flex">
            <select
              id="perPage"
              name="perPage"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:max-w-xs sm:text-sm sm:leading-6"
              onChange={handlePerPageChange}
              value={perPage}
            >
              {PerPageOptions.map((option, index) => (
                <option
                  key={index}
                  value={option}
                >
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div>
          {translate('per_page')}
          </div>
        </div>
        <div className="flex justify-center xl:justify-end">
          <div className="text-sm text-gray-700 flex items-center mr-3">
            {(currentPage - 1) * itemsPerPage + 1}
            {' '}
            -{' '}
            {Math.min(currentPage * itemsPerPage, totalRows)}
            {' '}
            {translate('of')}  {totalRows}
          </div>
          {totalPages > 1 &&
            <nav
              aria-label="Pagination"
              className="isolate inline-flex -space-x-px rounded-md shadow-sm"
            >
              {totalPages > 1 && currentPage > 1 && <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
              >
                <span className="sr-only">Previous</span>
                <BlackArrowLeftIcon
                  aria-hidden="true"
                  className="text-md text-black"
                />
              </button>}
              {<button
                onClick={() => onPageChange(1)}
                className={cn(
                  'relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 focus:outline-offset-0',
                  currentPage === 1 ? 'bg-primary-600 text-white' : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                )}
              >
                1
              </button>}
              {totalPages > LIMITPAGE && currentPage > 2 && (
                <button className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
                  ...
                </button>
              )}
              {totalPages > LIMITPAGE && currentPage > (totalPages - LIMITPAGE) && (
                [...Array(totalPages).keys()].slice(totalPages - LIMITPAGE, currentPage).map((page) => (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0`}
                  >
                    {page}
                  </button>
                )))}
              {[...Array(totalPages).keys()].slice(totalPages > LIMITPAGE && currentPage != 1 ? currentPage : 2, currentPage + LIMITPAGE).map((page) => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === page
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                    } focus:z-20 focus:outline-offset-0`}
                >
                  {page}
                </button>
              ))}
              {totalPages > LIMITPAGE && currentPage < (totalPages - LIMITPAGE) && (
                <button className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
                  ...
                </button>
              )}
              {currentPage - totalPages < LIMITPAGE && (
                <button
                  onClick={() => onPageChange(totalPages)}
                  className={cn(
                    'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0',
                    currentPage === totalPages && 'bg-primary-600 text-white'
                  )}
                >
                  {totalPages}
                </button>
              )}
              {totalPages > 1 && <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
              >
                <span className="sr-only">Next</span>
                <BlackArrowRightIcon
                  aria-hidden="true"
                  className="text-md text-black"
                />
              </button>}
            </nav>}
        </div>
      </div>
    </div>
  )
}

export default Pagination
